/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { ActionType, infigo } from '@dm/live-material-infigo';

import { SWITCH } from '../../../../../config';
import { $$default } from './grammar.type';

const map_from: (value: any) => Map<any, any> = (item) => {
    if (item instanceof Map) {
        return item;
    }
    if (Array.isArray(item)) {
        const map = new Map<string, string>();
        for (let i = 0; i < item.length; i++) {
            map.set(item[i], item[++i]);
        }
        return map;
    }
    if (typeof item === 'object') {
        const map = new Map<string, any>();
        Object.keys(item).forEach((key) => {
            map.set(item[key], key);
        });
        return map;
    }

    return undefined;
};
const isChinese = (value): boolean => /^[\u4e00-\u9fa5]+$/.test(value);
const defaultZhBoolMap = map_from({ true: '是', false: '否' });
const defaultEnBoolMap = map_from({ true: 'YES', false: 'NO' });
const defaultSymbolMap = map_from({ '::': '=', '!!': '!=' });
export const tip = (value): void => {
    infigo.store.dispatch({ type: ActionType.UpdateTips, value });
};
export const error = (err: { type: string; value: string; tip?: string }): void => {
    infigo.store.dispatch({ type: ActionType.UpdateError, value: err });
    throw new Error(err.type);
};
export const detach = (grammarListResult): any => {
    const grammarList = [];
    const apis = Object.keys(grammarListResult);
    apis.forEach((api) => {
        for (const grammar of grammarListResult[api]) {
            // value 不存在时候默认与key 一样
            if (!grammar.value) {
                grammar.value = grammar.key;
            }
            // map 与 enumMap 需要转换为Map型数据
            if (grammar.map) {
                grammar.map = map_from(grammar.map);
            }
            if (grammar.enumValue) {
                grammar.enumValue = map_from(grammar.enumValue);
            }
            // 对于布尔型的语法，需要为其补充默认的枚举
            if (grammar.type === 'boolean' && !grammar.enumValue) {
                grammar.enumValue = isChinese(grammar.key) ? defaultZhBoolMap : defaultEnBoolMap;
            }
            grammarList.push({ ...grammar, api });
        }
    });
    return grammarList;
};
export const add = (grammarListTree, type, grammarListResult): void => {
    grammarListTree[type] = detach(grammarListResult);
};
export const rebase = (grammarListA, grammarListB): void => {
    // 分解新增的GrammarList
    const grammarList = detach(grammarListB);
    grammarListA.forEach((grammar, index) => {
        const match = grammar.value === (grammarList[index].value || grammarList[index].key) ?
            grammarList[index] :
            grammarList.find((i) => i.vluae === grammar.value);
        if (!match) {
            throw new Error(`The two grammarList dost not match,maybe you leave out ${grammar.value} in the next GrammerList`);
        }
        if (typeof grammar.key === 'string') {
            grammar.key = [grammar.key];
        }
        if (!grammar.key.includes(match.key)) {
            grammar.key.push(match.key);
        }
        if (grammar.enumValue) {
            const enumValue: Array<any> = Array.from(grammar.enumValue).concat(Array.from(match.enumValue));
            grammar.enumValue = new Map(enumValue);
        }
    });
};
export const findMatchGrammar = (key, grammarList): any => {
    const matchList = [];
    const waitMatchKey = key.toLowerCase();
    const findMatch = (useKey, grammar): void => {
        const matchKey = useKey.toLowerCase();
        if (matchKey.indexOf(waitMatchKey) === 0) {
            matchList.push({
                matchItem: grammar,
                matchKey: useKey,
                searchKey: key,
                isCompleteMatch: matchKey === waitMatchKey,
            });
        }
    };
    grammarList.forEach((grammar) => {
        if (Array.isArray(grammar.key)) {
            return grammar.key.some((grammarKey) => {
                findMatch(grammarKey, grammar);
            });
        }
        if (typeof grammar.key === 'string') {
            findMatch(grammar.key, grammar);
        }

        return undefined;
    });
    return matchList;
};
export const findMatchEnum = (value, enumValue): any => {
    const matchList = [];
    const matchValue = value.value.toLowerCase();
    const keys: Array<string> = Array.from(enumValue.keys());
    for (const key of keys) {
        const matchKey = key.toLowerCase();
        if (matchKey.indexOf(matchValue) === 0) {
            matchList.push({
                [value.value]: key,
                [key]: enumValue.get(key),
                isCompleteMatch: matchValue === matchKey,
            });
        }
    }
    return matchList;
};
export const generateTips = (match, sourceType): void => {
    const tips = [];
    match.forEach((i) => {
        if (!i.isCompleteMatch) {
            tips.push({
                normal: `${sourceType} ${i.matchKey.slice(0, i.searchKey.length)}`,
                emphasis: i.matchKey.slice(i.searchKey.length) + ' ',
            });
        }
    });
    if (tips.length) {
        tip(tips);
    }
};
export const generateEnumTips = (matchEnum, value, pre): any => {
    const length = value.value.length;
    return tip(matchEnum.map((match) => ({
        normal: `${pre} ${match[value.value].slice(0, length)}`,
        emphasis: match[value.value].slice(length),
    })));
};
export const getSymbol = (grammar, symbol): any => {
    let actualSymbol = symbol;
    if (symbol === $$default) {
        actualSymbol = grammar.$default;
    }
    return grammar.map ? grammar.map.get(actualSymbol) : defaultSymbolMap.get(actualSymbol);
};
export const Transform = {
    blocks: (value, unit): any => {
        if (unit === 'GB') {
            return value * 2 * 1024 * 1024;
        }

        return undefined;
    },
};
export const transformValue = (type, value, unit?): number => {
    if (/(CAPACITY|CAPCITY|allocatedPoolQuota)/i.test(type)) {
        const defaultUnit = unit || 'GB';
        return Transform.blocks(value, defaultUnit);
    }
    return Number(value);
};
export const numSymbolToGrammar = (key, symbol, value, api, isTime = false): any => {
    let formValue;
    const tValue = transformValue(key, value);
    switch (symbol) {
        case '>':
            formValue = isTime ? `[${tValue + 24 * 60 * 60 + 1},]` : `[${tValue + 1},]`;
            break;
        case '<':
            formValue = `[,${tValue - 1}]`;
            break;
        case '>=':
            formValue = `[${tValue},]`;
            break;
        case '<=':
            formValue = `[,${tValue}]`;
            break;
        case '=':
            formValue = isTime ? `[${tValue},${tValue + 24 * 60 * 60}]` : `[${tValue},${tValue}]`;

        // no default
    }
    return {
        field: key,
        matchMode: ':',
        value: formValue,
        api,
    };
};
export const checkLicense = (check, licenseList): any => licenseList
    .filter((i) => {
        if (typeof i === 'string') {
            return true;
        } else {
            const condition1 = !i.license || check(i.license);
            const condition2 = !i.isNASFeature || SWITCH.SUPPORT_NAS;
            return Boolean(condition1 && condition2);
        }
    });
